import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue
} from "@/components/ui/select";

const today = () => new Date().toISOString().slice(0, 10);
const money = v => "Rp" + Number(v || 0).toLocaleString("id-ID");

export default function FixedAssets() {
  const [tab, setTab] = useState("list");
  const [rows, setRows] = useState([]);
  const [coa, setCoa] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState("");
  const [history, setHistory] = useState([]);
  const [period, setPeriod] = useState(today().slice(0, 7));

  const [form, setForm] = useState({
    asset_code: "", name: "", category: "",
    description: "", location: "", custodian: "",
    outlet_id: "", acquisition_date: today(),
    acquisition_cost: "", residual_value: "0",
    useful_life_months: "48",
    asset_coa_code: "",
    accumulated_depreciation_coa_code: "",
    depreciation_expense_coa_code: ""
  });

  const [tx, setTx] = useState({
    asset_id: "", date: today(),
    payment_coa_code: ""
  });

  const [opening, setOpening] = useState({
    asset_id: "", date: today(),
    acquisition_cost: "",
    accumulated_depreciation: "0",
    equity_coa_code: ""
  });

  const [disposal, setDisposal] = useState({
    asset_id: "", date: today(), proceeds: "0",
    proceeds_coa_code: "", gain_coa_code: "",
    loss_coa_code: ""
  });

  const [transfer, setTransfer] = useState({
    asset_id: "", date: today(),
    outlet_id: "", location: ""
  });

  const load = async () => {
    try {
      const [a, c, o] = await Promise.all([
        api.get("/fixed-assets"),
        api.get("/coa"),
        api.get("/outlets")
      ]);
      setRows(a.data || []);
      setCoa(c.data || []);
      setOutlets(o.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memuat data aset");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (url, data, message) => {
    setBusy(true);
    try {
      await api.post(url, data);
      toast.success(message);
      await load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Proses gagal");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const coaOptions = coa.filter(x => x.active !== false).map(x => ({
    value: x.code,
    label: `${x.code} — ${x.name}`
  }));

  const outletOptions = outlets.map(x => ({
    value: x.id,
    label: x.name
  }));

  const activeAssets = rows.filter(x => x.status === "active");
  const selectedAsset = rows.find(x => x.id === selected);

  const field = (label, value, setter, type = "text") => (
    <label className="grid gap-1.5 text-sm">
      <span className="font-medium">{label}</span>
      <Input
        type={type}
        value={value ?? ""}
        onChange={e => setter(e.target.value)}
      />
    </label>
  );

  const select = (label, value, setter, options) => (
    <label className="grid gap-1.5 text-sm">
      <span className="font-medium">{label}</span>
      <Select value={value || ""} onValueChange={setter}>
        <SelectTrigger><SelectValue placeholder="Pilih..." /></SelectTrigger>
        <SelectContent>
          {options.map(x => (
            <SelectItem key={x.value} value={String(x.value)}>
              {x.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );

  const openCard = async id => {
    setSelected(id);
    try {
      const r = await api.get(`/fixed-assets/${id}/card`);
      setHistory(r.data.transactions || []);
      setTab("card");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal membuka kartu aset");
    }
  };

  const tabs = [
    ["list", "Daftar Aset"],
    ["new", "Data Baru"],
    ["acquisition", "Perolehan"],
    ["opening", "Saldo Awal"],
    ["depreciation", "Penyusutan"],
    ["disposal", "Pelepasan"],
    ["transfer", "Mutasi"],
    ["card", "Kartu Aset"]
  ];

  return (
    <div className="page-shell space-y-5">
      <div>
        <p className="text-xs text-muted-foreground">
          Finance & Accounting / Aset Tetap
        </p>
        <h1 className="text-2xl font-bold mt-2">Aset Tetap</h1>
        <p className="text-sm text-muted-foreground">
          Pengelolaan aset, perolehan, penyusutan, pelepasan, dan mutasi.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Total aset</p>
          <b className="text-xl">{rows.length}</b>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Aset aktif</p>
          <b className="text-xl">{activeAssets.length}</b>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Harga perolehan</p>
          <b>{money(rows.reduce((s, x) => s + Number(x.acquisition_cost || 0), 0))}</b>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-muted-foreground">Nilai buku</p>
          <b>{money(rows.reduce((s, x) => s + Number(x.book_value || 0), 0))}</b>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2 border-b pb-3">
        {tabs.map(([key, label]) => (
          <Button
            key={key}
            size="sm"
            variant={tab === key ? "default" : "outline"}
            onClick={() => setTab(key)}
          >
            {label}
          </Button>
        ))}
      </div>

      {tab === "list" && (
        <Card className="p-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                {["Kode", "Nama", "Kategori", "Tanggal", "Perolehan", "Akumulasi", "Nilai Buku", "Status", ""].map(x => (
                  <th className="p-2" key={x}>{x}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(x => (
                <tr key={x.id} className="border-b">
                  <td className="p-2">{x.asset_code}</td>
                  <td className="p-2">{x.name}</td>
                  <td className="p-2">{x.category}</td>
                  <td className="p-2">{x.acquisition_date}</td>
                  <td className="p-2">{money(x.acquisition_cost)}</td>
                  <td className="p-2">{money(x.accumulated_depreciation)}</td>
                  <td className="p-2">{money(x.book_value)}</td>
                  <td className="p-2">{x.status}</td>
                  <td className="p-2">
                    <Button size="sm" variant="outline" onClick={() => openCard(x.id)}>
                      Kartu
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Belum ada aset terdaftar.
            </p>
          )}
        </Card>
      )}

      {tab === "new" && (
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">Registrasi Aset</h2>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {field("Kode aset (kosong = otomatis)", form.asset_code, v => setForm({...form, asset_code:v}))}
            {field("Nama aset", form.name, v => setForm({...form, name:v}))}
            {field("Kategori", form.category, v => setForm({...form, category:v}))}
            {field("Tanggal perolehan", form.acquisition_date, v => setForm({...form, acquisition_date:v}), "date")}
            {field("Harga perolehan", form.acquisition_cost, v => setForm({...form, acquisition_cost:v}), "number")}
            {field("Nilai residu", form.residual_value, v => setForm({...form, residual_value:v}), "number")}
            {field("Umur manfaat (bulan)", form.useful_life_months, v => setForm({...form, useful_life_months:v}), "number")}
            {field("Lokasi", form.location, v => setForm({...form, location:v}))}
            {field("Penanggung jawab", form.custodian, v => setForm({...form, custodian:v}))}
            {select("Outlet", form.outlet_id, v => setForm({...form, outlet_id:v}), outletOptions)}
            {select("COA Aset", form.asset_coa_code, v => setForm({...form, asset_coa_code:v}), coaOptions)}
            {select("COA Akumulasi Penyusutan", form.accumulated_depreciation_coa_code, v => setForm({...form, accumulated_depreciation_coa_code:v}), coaOptions)}
            {select("COA Beban Penyusutan", form.depreciation_expense_coa_code, v => setForm({...form, depreciation_expense_coa_code:v}), coaOptions)}
          </div>
          {field("Keterangan", form.description, v => setForm({...form, description:v}))}
          <Button disabled={busy} onClick={async () => {
            const ok = await save("/fixed-assets", form, "Master aset berhasil disimpan");
            if (ok) {
              setForm({
                asset_code:"", name:"", category:"",
                description:"", location:"", custodian:"",
                outlet_id:"", acquisition_date:today(),
                acquisition_cost:"", residual_value:"0",
                useful_life_months:"48", asset_coa_code:"",
                accumulated_depreciation_coa_code:"",
                depreciation_expense_coa_code:""
              });
              setTab("list");
            }
          }}>
            Simpan Aset
          </Button>
        </Card>
      )}

      {tab === "acquisition" && (
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">Posting Perolehan</h2>
          {select("Pilih aset", tx.asset_id, v => setTx({...tx, asset_id:v}),
            rows.filter(x => !x.acquisition_posted && !x.opening_posted).map(x => ({
              value:x.id, label:`${x.asset_code} — ${x.name}`
            })))}
          {field("Tanggal transaksi", tx.date, v => setTx({...tx, date:v}), "date")}
          {select("COA Kas/Bank pembayaran", tx.payment_coa_code, v => setTx({...tx, payment_coa_code:v}), coaOptions)}
          <Button disabled={busy || !tx.asset_id || !tx.payment_coa_code} onClick={() => save(
            `/fixed-assets/${tx.asset_id}/acquisition`,
            {date:tx.date, payment_coa_code:tx.payment_coa_code},
            "Perolehan berhasil diposting"
          )}>
            Posting Perolehan
          </Button>
        </Card>
      )}

      {tab === "opening" && (
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">Saldo Awal Aset</h2>
          {select("Pilih aset", opening.asset_id, v => setOpening({...opening, asset_id:v}),
            rows.filter(x => !x.acquisition_posted && !x.opening_posted).map(x => ({
              value:x.id, label:`${x.asset_code} — ${x.name}`
            })))}
          {field("Tanggal saldo awal", opening.date, v => setOpening({...opening, date:v}), "date")}
          {field("Harga perolehan", opening.acquisition_cost, v => setOpening({...opening, acquisition_cost:v}), "number")}
          {field("Akumulasi penyusutan", opening.accumulated_depreciation, v => setOpening({...opening, accumulated_depreciation:v}), "number")}
          {select("COA Ekuitas Saldo Awal", opening.equity_coa_code, v => setOpening({...opening, equity_coa_code:v}), coaOptions)}
          <Button disabled={busy || !opening.asset_id || !opening.equity_coa_code} onClick={() => save(
            `/fixed-assets/${opening.asset_id}/opening-balance`,
            opening,
            "Saldo awal berhasil diposting"
          )}>
            Posting Saldo Awal
          </Button>
        </Card>
      )}

      {tab === "depreciation" && (
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">Penyusutan Bulanan</h2>
          <p className="text-sm text-muted-foreground">
            Metode garis lurus. Periode yang sudah diproses akan dilewati.
          </p>
          {field("Periode penyusutan", period, setPeriod, "month")}
          <Button disabled={busy} onClick={() => save(
            "/fixed-assets/depreciation/run",
            {period},
            "Proses penyusutan selesai"
          )}>
            Jalankan Penyusutan
          </Button>
        </Card>
      )}

      {tab === "disposal" && (
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">Pelepasan Aset</h2>
          {select("Pilih aset", disposal.asset_id, v => setDisposal({...disposal, asset_id:v}),
            activeAssets.filter(x => x.acquisition_posted || x.opening_posted).map(x => ({
              value:x.id, label:`${x.asset_code} — ${x.name}`
            })))}
          {field("Tanggal pelepasan", disposal.date, v => setDisposal({...disposal, date:v}), "date")}
          {field("Hasil penjualan (isi 0 jika tidak ada)", disposal.proceeds, v => setDisposal({...disposal, proceeds:v}), "number")}
          {select("COA Kas/Bank penerimaan", disposal.proceeds_coa_code, v => setDisposal({...disposal, proceeds_coa_code:v}), coaOptions)}
          {select("COA Laba pelepasan", disposal.gain_coa_code, v => setDisposal({...disposal, gain_coa_code:v}), coaOptions)}
          {select("COA Rugi pelepasan", disposal.loss_coa_code, v => setDisposal({...disposal, loss_coa_code:v}), coaOptions)}
          <Button disabled={busy || !disposal.asset_id || !disposal.gain_coa_code || !disposal.loss_coa_code || (Number(disposal.proceeds)>0 && !disposal.proceeds_coa_code)} onClick={() => save(
            `/fixed-assets/${disposal.asset_id}/disposal`,
            disposal,
            "Pelepasan aset berhasil diposting"
          )}>
            Posting Pelepasan
          </Button>
        </Card>
      )}

      {tab === "transfer" && (
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">Mutasi Aset</h2>
          {select("Pilih aset", transfer.asset_id, v => setTransfer({...transfer, asset_id:v}),
            activeAssets.map(x => ({value:x.id, label:`${x.asset_code} — ${x.name}`})))}
          {select("Outlet tujuan", transfer.outlet_id, v => setTransfer({...transfer, outlet_id:v}), outletOptions)}
          {field("Tanggal mutasi", transfer.date, v => setTransfer({...transfer, date:v}), "date")}
          {field("Lokasi detail", transfer.location, v => setTransfer({...transfer, location:v}))}
          <Button disabled={busy || !transfer.asset_id || !transfer.outlet_id} onClick={() => save(
            `/fixed-assets/${transfer.asset_id}/transfer`,
            transfer,
            "Mutasi aset berhasil disimpan"
          )}>
            Simpan Mutasi
          </Button>
        </Card>
      )}

      {tab === "card" && (
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">Kartu Aset</h2>
          {select("Pilih aset", selected, openCard, rows.map(x => ({
            value:x.id, label:`${x.asset_code} — ${x.name}`
          })))}
          {selectedAsset && (
            <>
              <div className="grid md:grid-cols-3 gap-3">
                <div><p className="text-xs text-muted-foreground">Harga perolehan</p><b>{money(selectedAsset.acquisition_cost)}</b></div>
                <div><p className="text-xs text-muted-foreground">Akumulasi penyusutan</p><b>{money(selectedAsset.accumulated_depreciation)}</b></div>
                <div><p className="text-xs text-muted-foreground">Nilai buku</p><b>{money(selectedAsset.book_value)}</b></div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left">
                    <th className="p-2">Tanggal</th>
                    <th className="p-2">Jenis</th>
                    <th className="p-2">Referensi</th>
                    <th className="p-2 text-right">Nominal</th>
                  </tr></thead>
                  <tbody>{history.map(x => (
                    <tr className="border-b" key={x.id}>
                      <td className="p-2">{x.date}</td>
                      <td className="p-2">{x.kind}</td>
                      <td className="p-2">{x.reference}</td>
                      <td className="p-2 text-right">{money(x.amount)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
